// MDT LIVE LOCATION hook — permission gate state + continuous GPS streaming.
// Split out of MDT.jsx (refactor only — logic unchanged).
import { useCallback, useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { speak, blip } from "@/lib/audio";

export function useLiveGps({ signedOn, myUnit, load }) {
  const [gpsPerm, setGpsPerm] = useState("unknown"); // unknown | prompt | granted | denied
  const [selfFix, setSelfFix] = useState(null);      // { lat, lng, accuracy, ts }
  // Officer CONSENT (additive): explicit opt-in/out via the MDT GPS option.
  // Defaults ON so the existing sign-on gate flow is unchanged.
  const [consent, setConsent] = useState(() => localStorage.getItem("mdt_gps_consent") !== "0");
  const lastFixRef = useRef(null);
  const gpsSessionStarted = useRef(false);

  // Location permission gate — checked right after MDT sign-on. Never collects
  // silently: the officer explicitly taps ALLOW LOCATION (browser prompt follows).
  useEffect(() => {
    if (!signedOn) return;
    if (!navigator.geolocation) { setGpsPerm("denied"); return; }
    let st = null;
    const apply = (state) => setGpsPerm(state === "granted" ? "granted" : state === "denied" ? "denied" : "prompt");
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" })
        .then((s) => { st = s; apply(s.state); s.onchange = () => apply(s.state); })
        .catch(() => setGpsPerm("prompt"));
    } else setGpsPerm("prompt");
    return () => { if (st) st.onchange = null; };
  }, [signedOn]);

  const gpsSessionEvent = useCallback((event) => {
    client.post("/gps/session", { unit: myUnit, event, device: navigator.userAgent.slice(0, 110) }).catch(() => {});
  }, [myUnit]);

  const allowLocation = () => {
    if (!navigator.geolocation) { toast.error("This device/browser has no location service."); return; }
    navigator.geolocation.getCurrentPosition((p) => {
      setGpsPerm("granted");
      const fix = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy ?? null, ts: Date.now() };
      lastFixRef.current = fix; setSelfFix(fix);
      gpsSessionEvent("permission_granted");
      toast.success("LOCATION ACTIVE — map centered on your position; dispatch now sees this unit live");
    }, () => {
      setGpsPerm("denied");
      gpsSessionEvent("permission_denied");
    }, { enableHighAccuracy: true, timeout: 15000 });
  };

  const refreshLocation = () => {
    gpsSessionEvent("manual_refresh");
    navigator.geolocation?.getCurrentPosition(async (p) => {
      const fix = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy ?? null, ts: Date.now() };
      lastFixRef.current = fix; setSelfFix(fix);
      try { await client.post("/gps/ping", { unit: myUnit, lat: fix.lat, lng: fix.lng, accuracy: fix.accuracy, source: "manual" }); } catch { /* transient */ }
      toast.success("Location refreshed");
    }, () => toast.error("GPS unavailable — showing last known position"), { enableHighAccuracy: true, timeout: 10000 });
  };

  // Live GPS tracking: while signed on WITH permission granted, CONTINUOUSLY watch
  // the device position and stream it to dispatch — posts immediately, then on every
  // meaningful movement (>8 m) or at most every 10 s. Server audits the session,
  // keeps a breadcrumb history and auto-marks ON SCENE within a block.
  useEffect(() => {
    if (!signedOn || !myUnit || !navigator.geolocation || gpsPerm !== "granted" || !consent) return;
    if (!gpsSessionStarted.current) { gpsSessionStarted.current = true; gpsSessionEvent("start"); }
    let lastPost = 0, lastLat = null, lastLng = null;
    const post = async (coords) => {
      const { latitude: lat, longitude: lng, accuracy, heading, speed } = coords;
      const prev = lastFixRef.current;
      const movedFromPrev = movedM(lat, lng);
      lastPost = Date.now(); lastLat = lat; lastLng = lng;
      let hdg = Number.isFinite(heading) ? heading : null;
      if (hdg == null && prev && movedFromPrev > 5) {
        const toR = Math.PI / 180, dLon = (lng - prev.lng) * toR, la1 = prev.lat * toR, la2 = lat * toR;
        hdg = (Math.atan2(Math.sin(dLon) * Math.cos(la2), Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLon)) / toR + 360) % 360;
      }
      const fix = { lat, lng, accuracy: Number.isFinite(accuracy) ? accuracy : null, ts: Date.now(),
        heading: hdg ?? prev?.heading ?? null, speed: Number.isFinite(speed) && speed >= 0 ? speed : null };
      lastFixRef.current = fix; setSelfFix(fix);
      try {
        const { data } = await client.post("/gps/ping", {
          unit: myUnit, lat, lng, accuracy: fix.accuracy,
          heading: Number.isFinite(heading) ? heading : null,
          speed: Number.isFinite(speed) ? speed : null, source: "gps" });
        if (data.smart_arrival?.arrived) { speak(`${myUnit} on scene.`); blip(); toast.success(`AUTO ON-SCENE — within 1 block`); load(); }
      } catch { /* ignore transient */ }
    };
    const movedM = (lat, lng) => {
      if (lastLat === null) return Infinity;
      const dy = (lat - lastLat) * 111320, dx = (lng - lastLng) * 111320 * Math.cos(lat * Math.PI / 180);
      return Math.sqrt(dx * dx + dy * dy);
    };
    const watchId = navigator.geolocation.watchPosition((pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      if (movedM(lat, lng) > 8 || Date.now() - lastPost > 10000) post(pos.coords);
    }, () => {}, { enableHighAccuracy: true, maximumAge: 3000, timeout: 15000 });
    // Fallback heartbeat for devices that pause watchPosition in background tabs.
    const iv = setInterval(() => {
      if (Date.now() - lastPost > 25000)
        navigator.geolocation.getCurrentPosition((p) => post(p.coords), () => {},
          { enableHighAccuracy: true, timeout: 8000, maximumAge: 5000 });
    }, 25000);
    return () => { navigator.geolocation.clearWatch(watchId); clearInterval(iv); };
  }, [signedOn, myUnit, gpsPerm, consent, load, gpsSessionEvent]);

  // End the live tracking session (audited); unit goes GPS-offline. Called at sign-off.
  const endGpsSession = async () => {
    if (gpsSessionStarted.current) {
      gpsSessionStarted.current = false;
      try { await client.post("/gps/session", { unit: myUnit, event: "end" }); } catch { /* best-effort */ }
    }
  };

  // Officer-driven GPS OPTION (additive): explicit consent question before tracking,
  // and a documented opt-out that takes the unit GPS-offline for dispatch.
  const enableTracking = () => {
    localStorage.setItem("mdt_gps_consent", "1");
    setConsent(true);
    allowLocation();
  };
  const disableTracking = async () => {
    localStorage.setItem("mdt_gps_consent", "0");
    setConsent(false);
    setSelfFix(null);
    await endGpsSession();
    toast("LOCATION TRACKING OFF — dispatch no longer sees this unit's live position. Tap GPS to re-enable.", { duration: 8000 });
  };

  return { gpsPerm, selfFix, lastFixRef, consent, allowLocation, refreshLocation,
    enableTracking, disableTracking, endGpsSession };
}
